import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

// Material-UI components
import {
    Box,
    Button,
    Divider,
    FormControl,
    Grid,
    InputLabel,
    Paper,
    Select,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';

// services
import AziendaService from "../services/AziendeService"; // <-- MODIFICATO

// Funzione di utilità per formattare le date
function formatDate(date) {
    if (!date) return null;
    const d = new Date(date);
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    const year = d.getFullYear();

    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;

    return [year, month, day].join('-');
}

// --- Componente Azienda ---
const Azienda = (props) => {
    const { id } = useParams();
    let navigate = useNavigate();
    const sede = props.user?.sede || ''; // Ottieni la sede dall'utente loggato
    const isAdmin = props.user?.roles?.includes("ROLE_ADMIN") || false;

    // Stato iniziale specifico per l'azienda
    const initialAziendaState = {
        id: null,
        ragione_sociale: '',
        partita_iva: '',
        codice_fiscale: '',
        settore: '',
        email: '',
        pec: '',
        telefono: '',
        sito_web: '',
        indirizzo: '',
        cap: '',
        comune: '',
        provincia: '',
        codice_sdi: '',
        nome_referente: '',
        cognome_referente: '',
        email_referente: '',
        telefono_referente: '',
        note: '',
        sede: sede, // Imposta la sede di default dall'utente loggato
    };

    const [currentAzienda, setCurrentAzienda] = useState(initialAziendaState);
    const [errors, setErrors] = useState({});

    // Funzione per caricare i dati di un'azienda esistente
    const getAzienda = id => {
        AziendaService.get(id)
            .then(response => {
                setCurrentAzienda(response.data);
            })
            .catch(e => {
                console.log(e);
            });
    };

    useEffect(() => {
        if (id) {
            getAzienda(id);
        }
    }, [id]);

    // Gestore per i cambiamenti negli input
    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setCurrentAzienda({ ...currentAzienda, [name]: value });

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    // Logica di validazione
    const validateForm = () => {
        let tempErrors = {};
        let isValid = true;

        if (!currentAzienda.ragione_sociale) {
            tempErrors.ragione_sociale = "La Ragione Sociale è obbligatoria.";
            isValid = false;
        }
        if (currentAzienda.partita_iva && !/^[0-9]{11}$/.test(currentAzienda.partita_iva)) {
            tempErrors.partita_iva = "La Partita IVA deve contenere 11 cifre.";
            isValid = false;
        }

        setErrors(tempErrors);
        return isValid;
    };

    // Funzione per salvare una nuova azienda
    const saveAzienda = () => {
        if (!validateForm()) {
            alert("Per favore, correggi gli errori nel form.");
            return;
        }
        AziendaService.create(currentAzienda)
            .then(response => {
                alert("Azienda salvata correttamente!");
                navigate(`/dashboard/aziende/${response.data.id}`);
            })
            .catch(e => {
                console.log(e);
                alert("Errore durante il salvataggio dell'azienda.");
            });
    };

    // Funzione per aggiornare un'azienda esistente
    const updateAzienda = () => {
        if (!validateForm()) {
            alert("Per favore, correggi gli errori nel form.");
            return;
        }
        AziendaService.update(currentAzienda.id, currentAzienda)
            .then(response => {
                alert("Azienda aggiornata correttamente!");
            })
            .catch(e => {
                console.log(e);
                alert("Errore durante l'aggiornamento dell'azienda.");
            });
    };

    // Funzione per eliminare un'azienda
    const deleteAzienda = () => {
        if (window.confirm("Sei sicuro di voler eliminare questa azienda?")) {
            AziendaService.remove(currentAzienda.id)
                .then(() => {
                    navigate("/dashboard/aziende");
                })
                .catch(e => {
                    console.log(e);
                    alert("Errore durante l'eliminazione dell'azienda.");
                });
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
                {/* --- SEZIONE DATI AZIENDALI --- */}
                <Typography variant="h4" gutterBottom sx={{ textTransform: 'uppercase' }}>
                    {id ? "Modifica Azienda" : "Nuova Azienda"}
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <TextField name="ragione_sociale" label="Ragione Sociale" fullWidth value={currentAzienda.ragione_sociale} onChange={handleInputChange} error={!!errors.ragione_sociale} helperText={errors.ragione_sociale} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField name="partita_iva" label="Partita IVA" fullWidth value={currentAzienda.partita_iva} onChange={handleInputChange} error={!!errors.partita_iva} helperText={errors.partita_iva} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField name="codice_fiscale" label="Codice Fiscale" fullWidth value={currentAzienda.codice_fiscale} onChange={handleInputChange} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField name="settore" label="Settore" fullWidth value={currentAzienda.settore} onChange={handleInputChange} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField name="email" label="Email Azienda" type="email" fullWidth value={currentAzienda.email} onChange={handleInputChange} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField name="telefono" label="Telefono Azienda" fullWidth value={currentAzienda.telefono} onChange={handleInputChange} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField name="pec" label="PEC" type="email" fullWidth value={currentAzienda.pec} onChange={handleInputChange} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField name="codice_sdi" label="Codice SDI" fullWidth value={currentAzienda.codice_sdi} onChange={handleInputChange} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField name="sito_web" label="Sito Web" fullWidth value={currentAzienda.sito_web} onChange={handleInputChange} />
                    </Grid>
                </Grid>

                {/* --- SEZIONE REFERENTE --- */}
                <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>Dati Referente</Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <TextField name="nome_referente" label="Nome Referente" fullWidth value={currentAzienda.nome_referente} onChange={handleInputChange} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField name="cognome_referente" label="Cognome Referente" fullWidth value={currentAzienda.cognome_referente} onChange={handleInputChange} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField name="email_referente" label="Email Referente" type="email" fullWidth value={currentAzienda.email_referente} onChange={handleInputChange} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField name="telefono_referente" label="Telefono Referente" fullWidth value={currentAzienda.telefono_referente} onChange={handleInputChange} />
                    </Grid>
                </Grid>

                {/* --- SEZIONE INDIRIZZO --- */}
                <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>Indirizzo Sede</Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <TextField name="indirizzo" label="Indirizzo" fullWidth value={currentAzienda.indirizzo} onChange={handleInputChange} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField name="comune" label="Comune" fullWidth value={currentAzienda.comune} onChange={handleInputChange} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField name="provincia" label="Provincia (Sigla)" fullWidth value={currentAzienda.provincia} onChange={handleInputChange} inputProps={{ maxLength: 2 }} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField name="cap" label="CAP" fullWidth value={currentAzienda.cap} onChange={handleInputChange} />
                    </Grid>
                </Grid>

                {/* --- BOTTONI DI AZIONE --- */}
                <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        onClick={id ? updateAzienda : saveAzienda}
                    >
                        {id ? "Aggiorna" : "Salva"}
                    </Button >
                    {id && (
                        <Button variant="outlined" color="error" onClick={deleteAzienda} disabled={!isAdmin}>
                            Elimina
                        </Button>
                    )}
                </Box>
            </Paper>
        </Box>
    );
};

export default Azienda;